import type { User } from "@liveblocks/client"
import type { Repository } from "@prismicio/editor-fields"
import { getRequestCredentials, request } from "@prismicio/editor-support/Http"
import { queryOptions } from "@tanstack/react-query"
import { z } from "zod"

import { env } from "../env"
import { getRepositoryName } from "../routing"

export async function getLiveblocksSession() {
  const url = new URL("session-token", env.PRISMIC_LIVEBLOCKS_SERVICE_BASE_URL)
  url.searchParams.set("repository", getRepositoryName())

  return request(url, {
    credentials: getRequestCredentials(env.PRISMIC_PAGE_BUILDER_AUTH_STRATEGY),
    okType: LiveblocksSession,
  })
}

const LiveblocksSession = z.object({
  token: z.string(),
})

export function getLiveblocksSessionOptions() {
  return queryOptions({
    queryKey: ["liveblocksSession"],
    queryFn: () => getLiveblocksSession(),
    // Session (token) is valid for 1 hour.
    // We can cache it for 59 minutes to be safe.
    staleTime: 59 * 60 * 1000, // 59 minutes
    gcTime: 59 * 60 * 1000, // 59 minutes
  })
}

interface resolveUsersArgs {
  userIds: string[]
  /**
   * Authors from the repository
   *
   * Instead of directly querying the authors from the repository, we pass them
   * as an argument to avoid unnecessary queries and to utilize the cache
   */
  authors: Repository["authors"]
}

export function resolveUsers(args: resolveUsersArgs): User["info"][] {
  const { userIds, authors } = args

  const users = authors.filter((author) => userIds.includes(author.id))

  // TODO: handle removed users mentions
  // [PBD-2023](https://linear.app/prismic/issue/PBD-2033/handle-removed-users-mentions)
  return users.map((user) => ({
    name: getMentionDisplayName(user),
    avatar: user.uploadedAvatar,
    color: undefined,
    firstname: undefined,
    lastname: undefined,
    email: undefined,
    themeColor: undefined,
  }))
}

function getMentionDisplayName(user: Repository["authors"][number]) {
  if (!user.firstname) return user.email
  if (!user.lastname) return user.firstname

  return `${user.firstname} ${user.lastname}`
}

interface ResolveMentionSuggestionsArgs {
  /**
   * Text for searching the authors when mentioning
   *
   * @example User types `@john` in Liveblocks Composer, text is `john`
   */
  text: string
  /**
   * Authors from the repository
   *
   * Instead of directly querying the authors from the repository, we pass them
   * as an argument to avoid unnecessary queries and to utilize the cache
   */
  authors: Repository["authors"]
}

export function resolveMentionSuggestions(args: ResolveMentionSuggestionsArgs) {
  const { text, authors } = args

  return authors
    .filter((author) =>
      author.email?.toLowerCase().includes(text.toLowerCase()),
    )
    .map((author) => author.id)
}
